import { budgetFor } from "@authe/fe-commons/budgets";
import { DEFAULT_CONTRIBUTION_POLICY, type ContributionPolicy } from "@authe/saved-views";
/**
 * Investigations port — the index of collaborative investigation case files.
 *
 * An investigation is a watch scope (what the team is following) plus a case
 * file (pinned evidence, shared notes, live discussion, an approval runbook).
 *
 * **Backed by `investigation-mgmt-api`.** Cases used to be `SavedView` rows at
 * `tableId: "investigations"`, with everything crammed into `state.extra`. That
 * made them visible to teammates — which the earlier `localStorage` index never
 * did — but it cost three things a case file needs and a view does not:
 *
 * 1. **No point read.** Saved views expose no by-id lookup, so `get(id)` listed
 *    every case in the tenant and searched client-side.
 * 2. **No room to grow.** Evidence, annotations, comments and workflow steps are
 *    all unbounded, and a saved view is one 400 KB DynamoDB item.
 * 3. **No verb of its own.** `shared-graphs:edit` authorized both "rename a saved
 *    graph" and "publish this case's annotation to the whole tenant".
 *
 * Each of those is now a route: `GET /investigations/:id`, four child collections
 * with their own tables, and an `investigations:*` scope family.
 *
 * The wire is **snake_case** (the backend's canonical field names, generated from
 * the Pydantic models); this module is the one place that translates, so nothing
 * in `features/investigations` has to know. Watch shapes are still validated on
 * read — the server canonicalises on write, but a row written by an older shape
 * must degrade to "not shown" rather than take down the list route it appears in.
 */
import { poisoningPolicyWithinLimits, type PoisoningPolicy } from "@authe/tx-table";
import { unwrapData, type ApiClient } from "@authe/fe-commons/api";
import {
  INVESTIGATION_CONTRACT,
  type EvidenceRefTypeId,
  type EvidenceRoleId,
  type InvestigationScopeId,
} from "@authe/fe-commons/models";
import { asStringList } from "./wire";
import { parseActs, type CaseAct } from "@/features/investigations/caseActs";
import { isTxTenancyScopeId, type TxTenancyScopeId } from "@/features/transactions/scopes";
import type { AnalysisContext, AnalysisCapture, TimeRangePreset } from "@authe/fe-commons/metadata-analytics";
import { isCaseActorRole,
  DEFAULT_INVESTIGATION_STATE,
  DEFAULT_RETENTION,
  initialCaseRetention,
  EMPTY_FUNDS_FLOW_SETTINGS,
  FEED_WINDOW_PRESETS,
  HIDE_RULE_ID_PATTERN,
  MAX_HIDE_RULES,
  WINDOW_ALL,
  type CaseFundsFlowSettings,
  type FeedWindow,
  isCaseSagaKind,
  type InvestigationSaga,
  type InvestigationState,
  type InvestigationStatus,
  type CasePeriod,
  type Retention,
  type Watch,
} from "@/features/investigations/model";

/** Canonical FE path; `RESOURCE_REWRITES` maps it onto the service base path. */
const BASE = "/investigations";

/**
 * The reverse index's own prefix.
 *
 * Not under `BASE`: the question is "which cases pinned this", which has no case
 * to be under. `RESOURCE_REWRITES` maps it ahead of `/investigations` for exactly
 * that reason.
 */
const REFERENCES = "/evidence/references";
const evidenceListeners = new WeakMap<ApiClient, Set<() => void>>();
function notifyEvidenceChanged(client: ApiClient): void {
  evidenceListeners.get(client)?.forEach((listener) => listener());
}

/**
 * Rows per request on a case's child collections and the reverse index — the
 * route's own ceiling (`MAX_CHILD_PAGE_LIMIT`), which is the ask.
 */
const EVIDENCE_PAGE_SIZE: number = INVESTIGATION_CONTRACT.page_limits.max_child;

/**
 * How many pages of one case's exhibits to walk.
 *
 * 500 exhibits is far past the point where a case is a case rather than a
 * dataset, and the bound is what keeps opening a runaway one from costing an
 * unbounded number of requests. The walk reports when it stopped here, so the
 * shortfall is visible rather than read as "that's all of them".
 */
export const MAX_EVIDENCE_PAGES = 5;

/**
 * The tenancy RUNG a case is stored at — the server's own enum
 * (`commons…investigation.InvestigationScope`: user → tenant → org → customer →
 * system), read off the generated contract so a rung the server adds is a type
 * error here rather than a case quietly filed under the wrong heading.
 *
 * The create picker still offers only `user` and `tenant` — the two a person
 * chooses between — but a case CAN sit at `org`, `customer` or `system`
 * (administrative rungs, moved there by a PATCH with `investigations:share`),
 * and every surface that shows a scope shows the real one. `parseScope` used to
 * fold the three wider rungs into `tenant`, which read an org-wide case as
 * "shared with this tenant": true, but not what it is.
 *
 * Declared here rather than aliased to `@authe/saved-views`' `ViewScope`:
 * investigations left the saved-view shape (own repo pair, own
 * `investigations:*` scope family) and `ViewScope` carries a `team` tier a case
 * can never hold — a team is a carrier (`visible_to_team_ids`), not a rung.
 */
export type InvestigationScope = InvestigationScopeId;

export interface Investigation extends InvestigationState {
  /** Admin-managed filters that readers cannot switch off in the shared case. */
  lockedHideRules?: readonly string[];
  contributionPolicy?: ContributionPolicy;
  id: string;
  title: string;
  /** Free text about the case; `null` when nobody has written one. */
  description: string | null;
  revision?: number;
  scope: InvestigationScope;
  /**
   * Whether THIS caller may edit THIS case — the server's ABAC answer ANDed
   * with its RBAC one (`can_edit` on the wire).
   *
   * A scope list cannot give it: `investigations:edit` says the caller may edit
   * cases, not that this case is theirs to edit — a teammate's personal case
   * shared read-only arrives with the scope and without the right. `true` only
   * when the field is absent, which it never is from the current server.
   */
  canEdit: boolean;
  /** Auth0 sub of the creator, when the backend recorded one. */
  createdBy: string | null;
  /**
   * Whose case this is, on the `user` rung. `null` on a shared rung, where the
   * case belongs to the rung rather than to a person (`owner_for_rung`).
   *
   * Distinct from {@link createdBy}, which records who typed it into existence
   * and never moves. Ownership is what decides whether the caller may re-share.
   */
  ownerUserId: string | null;
  /**
   * Teams this case is shared with — the ABAC carrier, not a rung.
   *
   * The rung (`scope`) picks the PARTITION: `user` is the author's own, `tenant`
   * is everybody's. The carrier picks who, inside that partition, the case is
   * actually for. So a `user`-rung case naming a team is the team's case: it
   * stays out of the tenant-wide list and opens for that team's members, which
   * is the shape "my team works this one" has always needed.
   *
   * Empty on a `tenant`-rung case by construction — the rung already reaches
   * everyone there, so naming a team inside it would only ever narrow nothing.
   */
  visibleToTeamIds: string[];
  /**
   * People this case is shared with directly — the other ABAC carrier.
   *
   * Same partition rule as {@link visibleToTeamIds}: on the `user` rung these
   * are the colleagues admitted to one person's case; on a shared rung the rung
   * already reaches them. The audience sentence counts them, so "Only you and
   * 1 team" does not omit the two people the author also named.
   */
  visibleToUserIds: string[];
  /**
   * The collaboration room the case's live document lives in. Derived by the
   * server (a cross-language key), never rebuilt here.
   */
  roomId: string | null;
  /** Server-authenticated actor of the latest persisted case edit. */
  updatedBy?: string | null;
  /**
   * How many exhibits the case holds, maintained by the server.
   *
   * A COUNT, not the ids: the ids live in the evidence collection and are read
   * through {@link InvestigationsSource.listEvidence}. This replaces a
   * `pinned: string[]` that the backend stopped sending when evidence moved out
   * of the case row — it parsed to `[]` on every case, so every surface reading
   * it (the graph's pinned-only filter, the retention notice, the list chip)
   * had been quietly answering "none" for every case in the tenant.
   */
  evidenceCount: number;
  /** Comments on the case, maintained by the server like {@link evidenceCount}. */
  commentCount: number;
  /** Workflow steps not yet done — the "3 open steps" chip without a workflow read. */
  openStepCount: number;
  /**
   * The case's scope in the ONE shape every hand-off consumes.
   *
   * Server-derived and sent on every row, because when each hand-off derived
   * its own they disagreed — one took every address, one took the FIRST, one
   * took none. `caseHandoffs.ts` keeps the local derivations for the surfaces
   * that need them synchronously; this is the authority the two are checked
   * against.
   */
  handoff: CaseHandoff;
  createdAt: number;
  updatedAt: number;
}

export interface CreateInvestigationInput {
  /** Reader scope is independent of who may open the case. */
  feedScope?: TxTenancyScopeId;
  requestId?: string;
  title: string;
  scope: InvestigationScope;
  watches?: Watch[];
  retention?: Partial<Retention>;
  /** What the case is about — the period its runs ingest. Omitted means unstated. */
  period?: CasePeriod | null;
  saga?: InvestigationSaga;
}

/** What a case hands to the rest of the product, derived once on the server (`handoff` on every row). */
export interface CaseHandoff {
  investigationId: string;
  /** CAIP-10 accounts the case follows directly. */
  addresses: string[];
  protocolIds: string[];
  chainIds: string[];
  /**
   * Watches whose subject no concrete scope can express — an entity, tag, label
   * or group can cover addresses nobody enumerated. Reported rather than
   * expanded: resolving one would build a scope the reader never chose. `key`
   * is the server's stable spelling of the watch, for joining back to it.
   */
  unrepresented: { kind: string; catalogId: string; name: string | null; key: string | null }[];
  feedScope: TxTenancyScopeId;
  pipelineId: string | null;
}

/** What a redraw of the server's evidence graph reports (`POST …/evidence-graph`). */
export interface EvidenceGraphRedraw {
  /** Exhibits drawn into the graph. */
  exhibits: number;
  /** Drawable exhibits still without a drawing: their transaction is not decoded. */
  withoutScene: number;
  /** Drawn exhibits left out to stay under the size budget. */
  leftOut: number;
  /** Drawings this redraw captured. */
  captured: number;
  /** Milliseconds. */
  builtAt: number;
}

/** What a case can pin — `commons…investigation_evidence.EvidenceRefType`, off the generated contract. */
export type EvidenceRefType = EvidenceRefTypeId;

/**
 * A cross-chain transfer as an evidence reference: `{sagaKind}:{correlationId}`
 * — the saga's own `key` (`DerivedSaga.key`), which both legs carry. Not the
 * grouping engine's group id: a transfer the engine has not grouped yet is still
 * a transfer a story step can cite.
 */
export const SAGA_REF_TYPE: EvidenceRefType = "saga";

/**
 * What a case is SAYING about a reference.
 *
 * Pinning in and ruling out are the same shape of statement — this case, this
 * reference, this author, this reason — so they share one collection. An
 * analyst who excludes a transaction has made a judgement as durable and as
 * worth auditing as one who pins it, and both need the note, the reverse lookup
 * and the retention exemption.
 *
 * Nothing derived may write `excluded`. The dust filter proposes; a person
 * decides. Once "the machine guessed" and "an analyst decided" are stored the
 * same way, the case cannot tell a finding from a filter.
 */
export type EvidenceRole = EvidenceRoleId;

/** One judgement a case has made about one reference. */
export interface CaseEvidence {
  findingSnapshot?: Record<string, unknown>;
  origin?: Record<string, unknown>;
  analysisCapture?: AnalysisCapture;
  id: string;
  investigationId: string;
  /** Pinned in, or ruled out. Defaults to `exhibit` on rows written before roles. */
  role: EvidenceRole;
  refType: EvidenceRefType;
  /** Canonical id of the referenced thing — a canonical tx id, a CAIP-10 account… */
  refId: string;
  chainId: string | null;
  /** The analyst's reason. The point of the table, and why it is not a string array. */
  note: string | null;
  /**
   * Caption captured AT PIN TIME.
   *
   * Stored rather than resolved on read so a case's exhibit list still reads
   * when the referenced row is unavailable to whoever is looking.
   */
  label: string | null;
  pinnedBy: string | null;
  createdAt: number | null;
  updatedBy?: string | null;
  updatedAt?: number | null;
  /**
   * Which parts of the case's argument cite this exhibit — `act:<id>` today.
   *
   * Namespaced so a later surface with its own buckets cannot collide with the
   * story's. A list rather than one slot because an exhibit is unique per
   * `(case, reference)`: with a single field, citing a transaction in a second
   * step would silently un-cite it from the first.
   */
  slots: string[];
}

/**
 * One case's exhibits, and whether that is all of them.
 *
 * The truncation flag is the point. The set feeds the feed's retention
 * exemption, where a missing exhibit is not a shorter list but a pinned row
 * quietly evicted from the case — so a caller has to be able to tell "no more"
 * from "stopped looking".
 */
export interface CaseEvidencePage {
  items: CaseEvidence[];
  truncated: boolean;
}

/** A case that has pinned some reference — the reverse lookup's row. */
export interface CaseReference {
  investigationId: string;
  title: string;
  status: InvestigationStatus;
  evidence: CaseEvidence;
}

export interface PinEvidenceInput {
  findingRevision?: string;
  origin?: { surface: string };
  /** Omit to leave an existing judgement alone; `exhibit` on a new row. */
  role?: EvidenceRole;
  refType: EvidenceRefType;
  refId: string;
  chainId?: string | null;
  /**
   * Strings, never `null`: the gateway validates the body against the swagger
   * fragment, which declares both as plain strings, so a `null` is a 400 at the
   * edge before any Lambda runs. Omitted preserves the stored value (re-pinning
   * is an update); `""` clears it; text replaces it.
   */
  note?: string;
  label?: string;
  /**
   * Slots to cite this exhibit into / remove it from.
   *
   * Merge ops rather than a whole-list replacement, because two analysts citing
   * the same transaction into two different steps is the ordinary case and
   * replacement would drop the first one's citation — a lost edit that looks
   * exactly like a step nobody filled in. Omitted means no change, as `note`
   * and `role` already do.
   */
  addSlots?: readonly string[];
  removeSlots?: readonly string[];
}

export interface CaseActivity {
  activity_id: string;
  occurred_at: number;
  actor_id: string | null;
  action: string;
  subject_id: string;
  details: Record<string, unknown>;
}
export interface ListInvestigationsOptions {
  cursor?: string | null;
  /** Rows per page; the contract's page cap by default. */
  limit?: number;
  /** Archived cases too (the list page offers them behind a switch). Default true. */
  includeArchived?: boolean;
}

export interface InvestigationsPage {
  rows: Investigation[];
  /** Resumes the listing; `null` when every rung is exhausted. */
  nextCursor: string | null;
}

/** Pages `listAll` reads before refusing: a server that kept handing back a cursor must not spin the browser. */
export const MAX_LIST_PAGES = 20;

export interface InvestigationsSource {
  /** Active authoritative detections to carry from an actor into a case. */
  findingsForAccount?(chain: string, address: string): Promise<Array<{ finding_id: string; revision: string; saga_kind: string }>>;
  /** Edit the shared display name without replacing the immutable capture. */
  renameAnalysis(id: string, evidenceId: string, name: string): Promise<CaseEvidence>;
  captureAnalysis(id: string, context: AnalysisContext, requestId: string, options?: { note?: string; previousCaptureId?: string }): Promise<CaseEvidence>;
  activity(id: string, cursor?: string): Promise<{ items: CaseActivity[]; next_cursor?: string | null }>;
  updateWatches(id: string, operations: { add?: Watch[]; remove?: Watch[] }): Promise<Investigation>;
  /**
   * One page of the investigations the caller can see, newest activity first
   * across every tenancy rung. Rows are SUMMARIES: the case without its
   * story, annotations, funds-flow settings, column layout, hide rules and
   * policies (those are `get(id)`'s). A page can be shorter than `limit`, or
   * empty while a cursor is still given; keep paging until `nextCursor` is
   * null.
   */
  list(options?: ListInvestigationsOptions): Promise<InvestigationsPage>;
  /**
   * Every investigation the caller can see, for a surface that needs the whole
   * set at once (an address's case connections). Bounded: past
   * `MAX_LIST_PAGES` pages it rejects rather than spin.
   */
  listAll(options?: Pick<ListInvestigationsOptions, "includeArchived">): Promise<Investigation[]>;
  get(id: string): Promise<Investigation | null>;
  create(input: CreateInvestigationInput): Promise<Investigation>;
  /**
   * Apply CHANGES to a case, sending only what changed.
   *
   * Takes a patch rather than the whole row because the route is a PATCH and a
   * case is co-edited: a caller handing over its local copy would send every
   * field as it last read them, reverting whatever a colleague changed in
   * between. Callers hold an `Investigation` and want to change one thing, so
   * the id is separate and the rest is a `Partial`.
   */
  update(id: string, changes: Partial<Investigation>): Promise<Investigation>;
  remove(id: string): Promise<void>;

  /**
   * Pin an exhibit onto a case.
   *
   * Its own route because evidence is its own collection — it stopped being a
   * `pinned: string[]` on the case when a case outgrew one DynamoDB item. That
   * transition left a gap this closes: the menu went on pinning through
   * `update()`, whose PATCH body deliberately omits `pinned`, so the request
   * succeeded and pinned nothing while the UI reported success.
   *
   * Idempotent by reference server-side: pinning the same transaction twice
   * updates the note rather than producing a second exhibit, so a caller never
   * has to check first.
   */
  pinEvidence(id: string, input: PinEvidenceInput): Promise<CaseEvidence>;
  unpinEvidence(id: string, evidenceId: string): Promise<void>;
  /**
   * Draw the case's evidence graph on the server, now — the one a LIVE
   * publication with the graph section shows. The server redraws it whenever
   * the evidence changes; this reaches what that cannot: exhibits pinned without
   * a drawing (captured here, as this user), and a case published before the
   * server drew graphs at all.
   */
  redrawEvidenceGraph(id: string): Promise<EvidenceGraphRedraw>;
  /**
   * One case's exhibits, newest first.
   *
   * Walked rather than read once: the route pages at 50 and caps at 100, and a
   * long-running case outgrows that. The walk is bounded and reports the bound,
   * because a set summed over "however many pages happened to load" is a fact
   * about pagination rather than about the case.
   */
  listEvidence(id: string): Promise<CaseEvidencePage>;
  /**
   * Which cases have pinned this reference — filtered to what the caller may see.
   *
   * The question an analyst asks the moment a hash turns up somewhere else, and
   * the one the old `pinned: string[]` shape could only answer by reading every
   * case. Authorization is sharp here: the index is deliberately tenancy-free,
   * so a case the caller cannot open is not reported as existing — not even as a
   * count, which would still disclose that somebody is investigating it.
   *
   * Walked like {@link listEvidence}, with the same page bound: the route pages
   * at 50 by default and the first read never followed `next_cursor`, so "In N
   * cases" capped at 50 and read as the total. The route's `count` is the
   * page's, not the index's, so it cannot stand in for the walk. Past the bound
   * this REJECTS rather than returning a prefix — the reader of a badge cannot
   * tell "stopped looking" from "that's all of them", and the hook keeps the
   * previous answer with the error beside it.
   */
  casesReferencing(refType: EvidenceRefType, refId: string): Promise<CaseReference[]>;
  /** Same-session writes, after HTTP cache invalidation. */
  subscribeEvidenceChanges?(listener: () => void): () => void;
}

/**
 * One evidence row off the wire, or `null` when it is unusable.
 *
 * Dropped rather than half-built for the same reason a malformed watch is: an
 * exhibit with no reference cannot be rendered, navigated to, or unpinned, and a
 * blank row in a case's evidence list reads as data loss rather than as a row
 * that should not have been sent.
 */
function evidenceOf(raw: unknown): CaseEvidence | null {
  if (!isRecord(raw)) return null;
  const id = str(raw.id);
  const investigationId = str(raw.investigation_id);
  const refId = str(raw.ref_id);
  if (!id || !investigationId || !refId) return null;
  // Both are enums the server writes on every row (`exhibit` / `transaction`
  // are its defaults, applied on write). A row without them, or with a value
  // this build does not know, is not one this server wrote — and reading it
  // as an exhibit would pin as evidence something the case may have ruled out.
  const role = str(raw.role);
  const refType = str(raw.ref_type);
  if (!role || !EVIDENCE_ROLES.has(role) || !refType || !EVIDENCE_REF_TYPES.has(refType)) return null;
  // Epoch MS, like every other timestamp this module hands out (the case-row
  // mapper below scales the same wire field). Unscaled, the exhibits panel
  // rendered every pin as "1/21/1970" — seconds read as milliseconds.
  const createdAt = Number(raw.created_at) * 1000;
  const updatedAt = raw.updated_at == null ? NaN : Number(raw.updated_at) * 1000;
  return {
    id,
    investigationId,
    role: role as EvidenceRole,
    refType: refType as EvidenceRefType,
    refId,
    chainId: str(raw.chain_id),
    note: str(raw.note),
    label: str(raw.label),
    pinnedBy: str(raw.pinned_by),
    createdAt: Number.isFinite(createdAt) ? createdAt : null,
    updatedBy: str(raw.updated_by),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : null,
    slots: asStringList(raw.slots),
    findingSnapshot: isRecord(raw.finding_snapshot) ? raw.finding_snapshot : undefined,
    origin: isRecord(raw.origin) ? raw.origin : undefined,
    analysisCapture: isRecord(raw.analysis_capture) ? raw.analysis_capture as unknown as AnalysisCapture : undefined,
  };
}

const EVIDENCE_ROLES: ReadonlySet<string> = new Set(INVESTIGATION_CONTRACT.enums.evidence_role);
const EVIDENCE_REF_TYPES: ReadonlySet<string> = new Set(INVESTIGATION_CONTRACT.enums.evidence_ref_type);
const SCOPES: ReadonlySet<string> = new Set(INVESTIGATION_CONTRACT.enums.scope);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function str(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function strList(value: unknown): string[] {
  return Array.isArray(value) ? value.map(str).filter((v): v is string => v !== null) : [];
}

/**
 * Validate one persisted watch, dropping anything malformed.
 *
 * The server canonicalises and rejects on write, so this is the READ side of the
 * same seam — and the two want opposite behaviour. On the way in a malformed
 * watch is a client bug worth reporting; on the way out a row from an older
 * shape should degrade to "not shown" rather than break the page it appears on.
 *
 * The wire is snake_case only. Every stored watch went through the server's
 * canonicaliser, so a camelCase spelling is not an older row — it is not a row
 * this server wrote — and is dropped like any other malformed one.
 *
 * `key` is the server's stable spelling of the watch, carried through so the
 * feed can join a lane's result back to the watch that asked for it.
 */
function parseWatch(raw: unknown): Watch | null {
  if (!isRecord(raw)) return null;
  const parsed = watchShapeOf(raw);
  if (!parsed) return null;
  const key = str(raw.key);
  return key ? { ...parsed, key } : parsed;
}

function watchShapeOf(raw: Record<string, unknown>): Watch | null {
  const kind = str(raw.kind);
  switch (kind) {
    case "protocol": {
      const protocolId = str(raw.protocol_id);
      if (!protocolId) return null;
      const chains = strList(raw.chains);
      return chains.length ? { kind, protocolId, chains } : { kind, protocolId };
    }
    case "address": {
      const account = str(raw.account);
      if (!account) return null;
      const chains = strList(raw.chains);
      const role = isCaseActorRole(raw.role) ? raw.role : undefined;
      return { kind, account, ...(chains.length ? { chains } : {}), ...(role ? { role } : {}) };
    }
    case "chain": {
      const chain = str(raw.chain);
      return chain ? { kind, chain } : null;
    }
    case "entity":
    case "tag":
    case "label":
    case "group": {
      const catalogId = str(raw.catalog_id);
      if (!catalogId) return null;
      const name = str(raw.name);
      return name ? { kind, catalogId, name } : { kind, catalogId };
    }
    default:
      return null;
  }
}

/** A watch on the wire. Chains are already canonical CAIP-2 from the server. */
function watchToWire(watch: Watch): Record<string, unknown> {
  switch (watch.kind) {
    case "protocol":
      return { kind: "protocol", protocol_id: watch.protocolId, chains: watch.chains ?? [] };
    case "address":
      return { kind: "address", account: watch.account, chains: watch.chains ?? [], ...(watch.role ? { role: watch.role } : {}) };
    case "chain":
      return { kind: "chain", chain: watch.chain };
    default:
      return { kind: watch.kind, catalog_id: watch.catalogId, name: watch.name };
  }
}

/**
 * Read a persisted feed window — the server's `FeedWindow` shape, nothing else.
 *
 * The server validates the window on write (a half-written or inverted range
 * is rejected there), so anything that does not read as one of its four kinds
 * falls back to the default rather than being guessed at.
 */
function parseFeedWindow(raw: unknown): FeedWindow {
  if (!isRecord(raw)) return DEFAULT_RETENTION.window;
  if (raw.kind === "all") return WINDOW_ALL;
  if (raw.kind === "rolling") {
    return FEED_WINDOW_PRESETS.includes(raw.preset as TimeRangePreset)
      ? { kind: "rolling", preset: raw.preset as TimeRangePreset }
      : DEFAULT_RETENTION.window;
  }
  if (raw.kind === "absolute") {
    const since = Number(raw.since);
    const until = Number(raw.until);
    // Both bounds required and ordered: a half-written absolute window would
    // otherwise read as "since epoch", quietly turning a saved historical range
    // into an unbounded scan of the whole partition.
    if (Number.isFinite(since) && Number.isFinite(until) && since > 0 && since <= until) {
      return { kind: "absolute", since: Math.floor(since), until: Math.floor(until) };
    }
  }
  if (raw.kind === "since") {
    const since = Number(raw.since);
    if (Number.isFinite(since) && since > 0) return { kind: "since", since: Math.floor(since) };
  }
  return DEFAULT_RETENTION.window;
}

function parseRetention(raw: unknown): Retention {
  if (!isRecord(raw)) return DEFAULT_RETENTION;
  const maxRows = Number(raw.max_rows);
  return {
    window: parseFeedWindow(raw.window),
    maxRows:
      Number.isFinite(maxRows) && maxRows > 0 ? Math.floor(maxRows) : DEFAULT_RETENTION.maxRows,
    // Only an explicit `false` turns it off, so a case saved before the field
    // existed gets the default rather than the falsy reading of `undefined`.
    balanceChains: raw.balance_chains !== false,
  };
}

/**
 * The case's subject period off the wire — seconds there, milliseconds here.
 *
 * `null` survives the round trip rather than becoming an empty object: "nobody
 * has said what period this case covers" is a real answer the run planner acts
 * on, and folding it into `{since: null, until: null}` would make the absence
 * indistinguishable from a period somebody cleared.
 */
function parsePeriod(raw: unknown): CasePeriod | null {
  if (!isRecord(raw)) return null;
  const secs = (v: unknown): number | null => {
    const n = Number(v);
    return Number.isFinite(n) && n > 0 ? n * 1000 : null;
  };
  const since = secs(raw.since);
  const until = secs(raw.until);
  return since === null && until === null ? null : { since, until };
}

function periodToWire(period: CasePeriod | null): Record<string, unknown> | null {
  if (!period || (period.since === null && period.until === null)) return null;
  return {
    ...(period.since !== null ? { since: Math.floor(period.since / 1000) } : {}),
    ...(period.until !== null ? { until: Math.floor(period.until / 1000) } : {}),
  };
}

function retentionToWire(retention: Retention): Record<string, unknown> {
  const { window } = retention;
  return {
    window:
      window.kind === "rolling"
        ? { kind: "rolling", preset: window.preset }
        : window.kind === "absolute"
          ? { kind: "absolute", since: window.since, until: window.until }
          : window.kind === "since"
            ? { kind: "since", since: window.since }
          : { kind: "all" },
    max_rows: retention.maxRows,
    balance_chains: retention.balanceChains,
  };
}

function parseSaga(raw: unknown): InvestigationSaga {
  // Any bridge family, not the two this used to allow — a saga kind the wire
  // carries but this build does not recognise is still the case's monitor, and
  // dropping it silently unfollowed what the author asked to follow.
  return isCaseSagaKind(raw) ? raw : null;
}

function parseStatus(raw: unknown): InvestigationStatus {
  return raw === "archived" ? "archived" : "open";
}

/**
 * The feed's tenancy tier, defaulted for cases saved before it was a field.
 *
 * Those cases were pinned to their VISIBILITY scope (`user` or `tenant`), so
 * inheriting that here would faithfully reproduce an empty feed: a transaction is
 * stored once per tier, and ingest lands in `global`. They get the default
 * instead.
 */
function parseFeedScope(raw: unknown): TxTenancyScopeId {
  return isTxTenancyScopeId(raw) ? raw : DEFAULT_INVESTIGATION_STATE.feedScope;
}

/**
 * The graph annotation layer, or `null` when the case has none.
 *
 * Validated harder than the rest: this body is folded onto every scene the case
 * renders, so a shape the renderer no longer understands would take the graph
 * down rather than just degrade it. A layer we cannot read is dropped — the
 * observed graph still draws.
 */
/**
 * The case's annotations, carried through unparsed.
 *
 * Deliberately NOT normalised here. The field holds one layer per subject, and a
 * case written before that holds a single flat body — telling them apart needs
 * the subject the reader is asking about, which this module does not know. Every
 * consumer goes through `annotationLayers` / `layerForSubject` instead, which
 * take both shapes and are the single place the two are reconciled.
 *
 * Parsing eagerly here is what the previous version did, and it would now
 * silently return `null` for every case in the new shape: a map of layers is not
 * a `SceneOverlay`, so the case would read as having no annotations at all.
 */
function parseAnnotations(raw: unknown): unknown {
  return isRecord(raw) ? raw : {};
}

/**
 * The case's table layout — a flat `{ columnId: visible }` map, or `null` when
 * nobody has chosen one. Non-boolean entries are dropped rather than coerced: a
 * junk value would silently hide a column, and no layout at all falls back to the
 * focus default, which is a better answer than a wrong one.
 */
function parseColumnVisibility(raw: unknown): Record<string, boolean> | null {
  if (!isRecord(raw)) return null;
  const columns: Record<string, boolean> = {};
  for (const [id, visible] of Object.entries(raw)) {
    if (typeof visible === "boolean") columns[id] = visible;
  }
  return Object.keys(columns).length > 0 ? columns : null;
}

/**
 * The case's switched-on hide rules, or `null` when the field is absent — a
 * server that does not store it yet, or a case nobody has chosen for.
 *
 * Shaped, not checked against the registry: an id this build does not know is
 * kept, so saving from here does not drop a rule a newer dashboard added. The
 * server folds the same way (`_hide_rules_of`).
 */
function parseHideRules(raw: unknown): string[] | null {
  if (!Array.isArray(raw)) return null;
  const ids = raw.filter(
    (id): id is string => typeof id === "string" && HIDE_RULE_ID_PATTERN.test(id),
  );
  return [...new Set(ids)].slice(0, MAX_HIDE_RULES);
}

/**
 * The case's poisoning threshold, or `null` for the default — including for a
 * server that does not store the field yet, or anything out of range.
 */
function parsePoisoningPolicy(raw: unknown): PoisoningPolicy | null {
  if (!isRecord(raw)) return null;
  const policy = { minSharedPrefix: Number(raw.min_shared_prefix), minSharedSuffix: Number(raw.min_shared_suffix) };
  // The address-poisoning check's own limits (the catalogue snapshot) — the
  // range the server clamps a case's threshold to. A value outside them is not
  // one this case can have saved; read it as "no threshold of its own".
  return poisoningPolicyWithinLimits(policy) ? policy : null;
}

/**
 * The rung, exactly as the server's enum spells it.
 *
 * Strict rather than folded: this used to read anything but `user` as
 * `tenant`, so an org-wide case was described as shared with this tenant and
 * an unknown value was described as shared at all. A value outside the enum is
 * a contract break — a server this build does not know — and is raised as one.
 */
function parseScope(raw: unknown): InvestigationScope {
  if (typeof raw === "string" && SCOPES.has(raw)) return raw as InvestigationScope;
  throw new Error(
    `investigation-mgmt-api returned an investigation scope this build does not know: ${JSON.stringify(raw)} (expected one of ${[...SCOPES].join(", ")})`,
  );
}

/** A non-negative server counter; `0` when the field is absent or unreadable. */
function counter(raw: unknown): number {
  const n = Number(raw);
  return Number.isFinite(n) ? Math.max(0, Math.floor(n)) : 0;
}

/** The server's hand-off scope for a row — present on every case it answers with. */
function handoffOf(raw: unknown, investigationId: string): CaseHandoff {
  const row = isRecord(raw) ? raw : {};
  const unrepresented = Array.isArray(row.unrepresented) ? row.unrepresented : [];
  return {
    investigationId: str(row.investigation_id) ?? investigationId,
    addresses: strList(row.addresses),
    protocolIds: strList(row.protocol_ids),
    chainIds: strList(row.chain_ids),
    unrepresented: unrepresented.filter(isRecord).map((w) => ({
      kind: str(w.kind) ?? "unknown",
      catalogId: str(w.catalog_id) ?? "",
      name: str(w.name),
      key: str(w.key),
    })),
    feedScope: parseFeedScope(row.feed_scope),
    pipelineId: str(row.pipeline_id),
  };
}

function investigationFromWire(row: unknown): Investigation | null {
  if (!isRecord(row)) return null;
  const id = str(row.id);
  if (!id) return null;

  const watches = Array.isArray(row.watches)
    ? row.watches.map(parseWatch).filter((w): w is Watch => w !== null)
    : [];
  const lastActivityAt = Number(row.last_activity_at) * 1000;
  const createdAt = Number(row.created_at) * 1000;
  const updatedAt = Number(row.updated_at ?? row.created_at) * 1000;

  return {
    id,
    title: str(row.title) ?? INVESTIGATION_CONTRACT.default_title,
    description: str(row.description),
    revision: Number(row.revision) || 0,
    scope: parseScope(row.scope),
    // The server's ABAC ∧ RBAC answer. `true` only when the field is ABSENT —
    // `false` is the one value that must never be read as anything else.
    canEdit: row.can_edit === undefined ? true : row.can_edit === true,
    createdBy: str(row.created_by),
    ownerUserId: str(row.owner_user_id),
    contributionPolicy: isRecord(row.contribution_policy) ? row.contribution_policy as unknown as ContributionPolicy : DEFAULT_CONTRIBUTION_POLICY,
    visibleToTeamIds: strList(row.visible_to_team_ids),
    visibleToUserIds: strList(row.visible_to_user_ids),
    roomId: str(row.room_id),
    updatedBy: str(row.updated_by),
    watches,
    retention: parseRetention(row.retention),
    period: parsePeriod(row.period),
    feedScope: parseFeedScope(row.feed_scope),
    status: parseStatus(row.status),
    saga: parseSaga(row.saga),
    annotations: parseAnnotations(row.annotations),
    columnVisibility: parseColumnVisibility(row.column_visibility),
    fundsFlow: parseFundsFlow(row.funds_flow),
    hideRules: parseHideRules(row.hide_rules),
    lockedHideRules: parseHideRules(row.locked_hide_rules) ?? [],
    poisoningPolicy: parsePoisoningPolicy(row.poisoning_policy),
    // A blank id reads as "the system default" rather than being carried through
    // as `""`, which would submit an empty pipeline_id that means the same thing
    // by accident instead of by decision.
    pipelineId: str(row.pipeline_id),
    // Validated per act rather than trusted: one malformed entry should be absent
    // from the narrative panel, not break it.
    acts: parseActs(row.acts),
    // Epoch SECONDS on the wire (matching `block_timestamp` and every other
    // timestamp the platform stores), MILLISECONDS in the FE. Getting this
    // backwards is a live hazard — a seconds value read as ms puts every case in
    // 1970, and an ms value read as seconds puts them in the year 55000.
    lastActivityAt: Number.isFinite(lastActivityAt) ? lastActivityAt : updatedAt,
    // The server's own counters. `pinned` used to be read here and has not been
    // sent since evidence became its own collection.
    evidenceCount: counter(row.evidence_count),
    commentCount: counter(row.comment_count),
    openStepCount: counter(row.open_step_count),
    handoff: handoffOf(row.handoff, id),
    createdAt: Number.isFinite(createdAt) ? createdAt : Date.now(),
    updatedAt: Number.isFinite(updatedAt) ? updatedAt : Date.now(),
  };
}

/**
 * The PATCH body for a set of CHANGES — only the fields actually being changed.
 *
 * It used to take a whole `Investigation` and emit all twelve mutable fields
 * every time, which made a PATCH route behave as a PUT. The docstring said the
 * opposite ("deliberately partial: a case is co-edited, and sending the whole
 * object would revert whatever a colleague changed between this client's read
 * and its write") and that is exactly what happened: a case is held in local
 * state, so toggling a column sent the retention window, the watch list, the
 * story acts and the annotation layer as this client last read them. A
 * colleague's edit in between was reverted by an action that had nothing to do
 * with it, silently, with no conflict anyone could see.
 *
 * `pinned` is absent for a different reason — evidence is its own collection
 * with its own routes, not a field on the case.
 */
function investigationToPatch(changes: Partial<Investigation>): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  // `in`, not a truthiness test: `status`, `saga` and `pipelineId` are all
  // legitimately cleared, and a falsy check would make clearing them impossible
  // — the same shape as the assignee that could never be unset.
  if ("contributionPolicy" in changes) body.contribution_policy = changes.contributionPolicy;
  if ("title" in changes) body.title = changes.title;
  if ("description" in changes) body.description = changes.description ?? null;
  if ("scope" in changes) body.scope = changes.scope;
  // The ABAC carriers. Sent whenever either is being changed — including to
  // `[]`, which is how a share is taken back; `in` rather than a truthiness
  // test, for the same reason `status` uses it.
  if ("visibleToTeamIds" in changes) {
    body.visible_to_team_ids = changes.visibleToTeamIds ?? [];
  }
  if ("visibleToUserIds" in changes) {
    body.visible_to_user_ids = changes.visibleToUserIds ?? [];
  }
  if ("status" in changes) body.status = changes.status;
  if ("watches" in changes) body.watches = (changes.watches ?? []).map(watchToWire);
  if ("retention" in changes && changes.retention) {
    body.retention = retentionToWire(changes.retention);
  }
  if ("period" in changes) body.period = periodToWire(changes.period ?? null);
  if ("feedScope" in changes) body.feed_scope = changes.feedScope;
  if ("saga" in changes) body.saga = changes.saga;
  if ("pipelineId" in changes) body.pipeline_id = changes.pipelineId;
  if ("acts" in changes) body.acts = changes.acts;
  if ("annotations" in changes) body.annotations = changes.annotations ?? {};
  if ("columnVisibility" in changes) body.column_visibility = changes.columnVisibility ?? {};
  if ("fundsFlow" in changes) body.funds_flow = fundsFlowToWire(changes.fundsFlow);
  if ("hideRules" in changes) body.hide_rules = [...(changes.hideRules ?? [])];
  if ("lockedHideRules" in changes) body.locked_hide_rules = [...(changes.lockedHideRules ?? [])];
  if ("poisoningPolicy" in changes) {
    // `null` is "follow the platform default" — sent, so a tuned case can be reset.
    body.poisoning_policy = changes.poisoningPolicy
      ? {
          min_shared_prefix: changes.poisoningPolicy.minSharedPrefix,
          min_shared_suffix: changes.poisoningPolicy.minSharedSuffix,
        }
      : null;
  }
  return body;
}

/**
 * The case's counterparty-summary settings, coerced per field.
 *
 * Never raises and never partially applies: a malformed entry is dropped and the
 * rest stand, because these are view settings and a case whose panel will not
 * open is worse than one whose exclusion list lost an entry. The server folds
 * the same way (`_funds_flow_of`), so a value that round-trips through a save is
 * the value that was read.
 */
function parseFundsFlow(raw: unknown): CaseFundsFlowSettings {
  if (!isRecord(raw)) return EMPTY_FUNDS_FLOW_SETTINGS;
  return {
    // Lower-cased on both sides of the wire: an exclusion is compared against a
    // row's counterparty, which arrives lower-cased, so a checksummed entry
    // would never match and the control would appear to do nothing.
    excluded: strList(raw.excluded_counterparties).map((a) => a.toLowerCase()),
    // Order preserved — it IS the setting, being what the panel renders first.
    pinnedCategories: strList(raw.pinned_categories).map((c) => c.toLowerCase()),
    pricedOnly: raw.priced_only === true,
  };
}

function fundsFlowToWire(
  settings: CaseFundsFlowSettings | null | undefined,
): Record<string, unknown> {
  const s = settings ?? EMPTY_FUNDS_FLOW_SETTINGS;
  return {
    excluded_counterparties: s.excluded,
    pinned_categories: s.pinnedCategories,
    priced_only: s.pricedOnly,
  };
}

function byActivityDesc(a: Investigation, b: Investigation): number {
  return Math.max(b.lastActivityAt, b.updatedAt) - Math.max(a.lastActivityAt, a.updatedAt);
}

/** The rows of a `{ items, next_cursor }` page — the one envelope every list route answers with. */
function itemsOf(payload: unknown): unknown[] {
  return isRecord(payload) && Array.isArray(payload.items) ? payload.items : [];
}

function investigationOf(payload: unknown): Investigation | null {
  if (!isRecord(payload)) return null;
  return investigationFromWire(payload.investigation ?? payload);
}

/**
 * The investigations index over `investigation-mgmt-api`.
 *
 * `list()` is ONE page: the server merges every rung newest-first and bounds
 * the page, so the client no longer drains the whole tenant and sorts locally.
 * A page can still come back short, or empty with a cursor, because private
 * and archived rows are dropped after the read (a `FilterExpression` would
 * make the page size unpredictable) and a rung whose visible head is not yet
 * known holds the merge — so a reader pages until `nextCursor` is null.
 */
export function createApiInvestigationsSource(client: ApiClient): InvestigationsSource {
  const listPage = async (options: ListInvestigationsOptions = {}): Promise<InvestigationsPage> => {
    const limit = Math.max(1, Math.min(options.limit ?? INVESTIGATION_CONTRACT.page_limits.max, INVESTIGATION_CONTRACT.page_limits.max));
    const query: Record<string, string> = { limit: String(limit), include_archived: String(options.includeArchived ?? true) };
    if (options.cursor) query.cursor = options.cursor;
    const payload = unwrapData(await client.get<Record<string, unknown>>(BASE, { query, cacheTtlMs: 0 }));
    const rows: Investigation[] = [];
    for (const raw of itemsOf(payload)) {
      const row = investigationFromWire(raw);
      if (row) rows.push(row);
    }
    return { rows, nextCursor: isRecord(payload) ? (str(payload.next_cursor) ?? null) : null };
  };
  const listAll = async (options: Pick<ListInvestigationsOptions, "includeArchived"> = {}): Promise<Investigation[]> => {
    const rows: Investigation[] = [];
    let cursor: string | null = null;
    for (let page = 0; page < MAX_LIST_PAGES; page += 1) {
      const next = await listPage({ cursor, includeArchived: options.includeArchived });
      rows.push(...next.rows);
      cursor = next.nextCursor;
      if (!cursor) break;
    }
    if (cursor) throw new Error("Investigation list exceeded its page limit; case references may be incomplete.");
    // Already in server order; the sort only settles ties across pages.
    return rows.sort(byActivityDesc);
  };

  return {
    list: listPage,
    listAll,

    async get(id) {
      const payload = unwrapData(
        await client.get<Record<string, unknown>>(`${BASE}/${encodeURIComponent(id)}`, { cacheTtlMs: 0 }),
      );
      return investigationOf(payload);
    },

    async create(input) {
      const payload = unwrapData(
        await client.post<Record<string, unknown>>(BASE, {
          ...(input.requestId ? { request_id: input.requestId } : {}),
          title: input.title.trim() || INVESTIGATION_CONTRACT.default_title,
          scope: input.scope,
          watches: (input.watches ?? []).map(watchToWire),
          retention: retentionToWire(initialCaseRetention(input.period ?? null, input.retention)),
          ...(periodToWire(input.period ?? null) ? { period: periodToWire(input.period ?? null) } : {}),
          saga: input.saga ?? null,
          feed_scope: input.feedScope ?? (input.scope === "tenant" ? "tenant" : "user"),
        }),
      );
      const created = investigationOf(payload);
      if (!created) throw new Error("investigation-mgmt-api returned no investigation");
      // Drop the short GET cache so the list reflects the new case immediately.
      client.invalidateMatching(BASE);
      return created;
    },

    async update(id, changes) {
      const path = `${BASE}/${encodeURIComponent(id)}`;
      const payload = unwrapData(
        await client.patch<Record<string, unknown>>(path, investigationToPatch(changes)),
      );
      const updated = investigationOf(payload);
      if (!updated) throw new Error("investigation-mgmt-api returned no investigation");
      client.invalidateMatching(BASE);
      client.invalidate(path);
      return updated;
    },

    async updateWatches(id, operations) {
      const payload = unwrapData(await client.patch<Record<string, unknown>>(`${BASE}/${encodeURIComponent(id)}`, {
        add_watches: (operations.add ?? []).map(watchToWire), remove_watches: (operations.remove ?? []).map(watchToWire),
      }));
      const updated = investigationOf(payload);
      if (!updated) throw new Error("No investigation returned");
      client.invalidateMatching(BASE);
      return updated;
    },

    async captureAnalysis(id, context, requestId, options = {}) {
      const payload = unwrapData(await client.post<Record<string, unknown>>(`${BASE}/${encodeURIComponent(id)}/evidence`, {
        ref_type: "analysis", ref_id: requestId, analysis_context: context,
        ...(options.note ? { note: options.note } : {}),
        ...(options.previousCaptureId ? { previous_capture_id: options.previousCaptureId } : {}),
      }));
      const saved = evidenceOf(payload.evidence);
      if (!saved?.analysisCapture) throw new Error("The server returned no captured analysis");
      client.invalidateMatching(BASE);
      notifyEvidenceChanged(client);
      return saved;
    },

    async renameAnalysis(id, evidenceId, name) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence/${encodeURIComponent(evidenceId)}`;
      const payload = unwrapData(await client.patch<Record<string, unknown>>(path, { label: name.trim() }));
      const saved = evidenceOf(payload.evidence);
      if (!saved?.analysisCapture) throw new Error("The server returned no saved analysis");
      client.invalidateMatching(BASE);
      client.invalidateMatching(REFERENCES);
      notifyEvidenceChanged(client);
      return saved;
    },

    async activity(id, cursor) {
      return unwrapData(await client.get<{ items: CaseActivity[]; next_cursor?: string | null }>(`${BASE}/${encodeURIComponent(id)}/activity`, { query: { limit: 50, cursor } }));
    },

    async remove(id) {
      const path = `${BASE}/${encodeURIComponent(id)}`;
      unwrapData(await client.delete<void>(path));
      client.invalidateMatching(BASE);
      client.invalidate(path);
    },

    async findingsForAccount(chain, address) {
      const findings: Array<{ finding_id: string; revision: string; saga_kind: string }> = [];
      let cursor: string | undefined;
      // Bounded: an exchange hot wallet can be named by thousands of dust and
      // poisoning findings, and a pin that follows every page would read them
      // all before the case could say anything. Five pages is 500 findings.
      let pages = 0;
      do {
        const page = unwrapData(await client.get<{ items: typeof findings; next_cursor?: string }>("/grouping/findings", { query: { chain, address, cursor, limit: budgetFor("findings.history").rows } }));
        findings.push(...page.items);
        cursor = page.next_cursor;
        pages += 1;
      } while (cursor && pages < 5);
      return findings;
    },

    async pinEvidence(id, input) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      const payload = unwrapData(
        await client.post<Record<string, unknown>>(path, {
          ref_type: input.refType,
          finding_revision: input.findingRevision,
          origin: input.origin,
          ref_id: input.refId,
          ...(input.role ? { role: input.role } : {}),
          ...(input.chainId ? { chain_id: input.chainId } : {}),
          // `undefined`, not falsy. Re-pinning is an UPDATE server-side, and it
          // keeps whatever field the body omits — so omitting on falsy would
          // make an empty note mean "leave the old one", and clearing a note
          // impossible. Omitted preserves; `""` clears; text replaces.
          ...(input.note !== undefined ? { note: input.note } : {}),
          ...(input.label !== undefined ? { label: input.label } : {}),
          ...(input.addSlots?.length ? { add_slots: [...input.addSlots] } : {}),
          ...(input.removeSlots?.length ? { remove_slots: [...input.removeSlots] } : {}),
        }),
      );
      const row = evidenceOf(isRecord(payload) ? payload.evidence : null);
      if (!row) throw new Error("investigation-mgmt-api returned no evidence row");
      // The case's `updatedAt` and `evidence_count` move with a pin (the server
      // touches both), and the reverse index now has a new entry — so the case
      // reads and every "pinned in N cases" badge for this reference are stale.
      //
      // `invalidateMatching`, not `invalidate`: the exact-URL form only reaches
      // a read the writer can name in full, query string included. The exhibit
      // list is read per page (`?limit=&cursor=`) and `REFERENCES` is a prefix
      // that is never itself a GET URL, so naming them exactly invalidated
      // nothing and the next read was served the pre-write body.
      client.invalidateMatching(BASE);
      client.invalidateMatching(path);
      client.invalidateMatching(REFERENCES);
      notifyEvidenceChanged(client);
      return row;
    },

    async redrawEvidenceGraph(id) {
      const raw = unwrapData(
        await client.post<Record<string, unknown>>(`${BASE}/${encodeURIComponent(id)}/evidence-graph`, {}),
      ) as Record<string, unknown>;
      const count = (key: string): number => (typeof raw[key] === "number" ? (raw[key] as number) : 0);
      return {
        exhibits: count("exhibits"),
        withoutScene: count("without_scene"),
        leftOut: count("left_out"),
        captured: count("captured"),
        builtAt: count("built_at") * 1000,
      };
    },
    async unpinEvidence(id, evidenceId) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      unwrapData(await client.delete<void>(`${path}/${encodeURIComponent(evidenceId)}`));
      client.invalidateMatching(BASE);
      client.invalidateMatching(path);
      client.invalidateMatching(REFERENCES);
      notifyEvidenceChanged(client);
    },

    subscribeEvidenceChanges(listener) {
      let listeners = evidenceListeners.get(client);
      if (!listeners) { listeners = new Set(); evidenceListeners.set(client, listeners); }
      listeners.add(listener);
      return () => { listeners.delete(listener); };
    },

    async listEvidence(id) {
      const path = `${BASE}/${encodeURIComponent(id)}/evidence`;
      const items: CaseEvidence[] = [];
      let cursor: string | null = null;
      let truncated = false;
      for (let page = 0; page < MAX_EVIDENCE_PAGES; page += 1) {
        const payload: unknown = unwrapData(
          await client.get<Record<string, unknown>>(path, {
            query: { limit: EVIDENCE_PAGE_SIZE, cursor: cursor ?? undefined },
            cacheTtlMs: 0,
          }),
        );
        const raw = isRecord(payload) && Array.isArray(payload.items) ? payload.items : [];
        for (const row of raw) {
          const parsed = evidenceOf(row);
          if (parsed) items.push(parsed);
        }
        cursor = isRecord(payload) ? str(payload.next_cursor) : null;
        if (!cursor) break;
        truncated = page === MAX_EVIDENCE_PAGES - 1;
      }
      return { items, truncated };
    },

    async casesReferencing(refType, refId) {
      if (!refId) return [];
      const path = `${REFERENCES}/${encodeURIComponent(refType)}/${encodeURIComponent(refId)}`;
      const out: CaseReference[] = [];
      let cursor: string | null = null;
      for (let page = 0; page < MAX_EVIDENCE_PAGES; page += 1) {
        const payload: unknown = unwrapData(
          await client.get<Record<string, unknown>>(path, {
            query: { limit: EVIDENCE_PAGE_SIZE, cursor: cursor ?? undefined },
          }),
        );
        const rows = isRecord(payload) && Array.isArray(payload.results) ? payload.results : [];
        for (const raw of rows) {
          if (!isRecord(raw)) continue;
          const evidence = evidenceOf(raw.evidence);
          const investigationId = str(raw.investigation_id);
          if (!evidence || !investigationId) continue;
          out.push({
            investigationId,
            title: str(raw.title) ?? INVESTIGATION_CONTRACT.default_title,
            status: parseStatus(raw.status),
            evidence,
          });
        }
        cursor = isRecord(payload) ? str(payload.next_cursor) : null;
        if (!cursor) return out;
      }
      throw new Error(
        `More than ${MAX_EVIDENCE_PAGES * EVIDENCE_PAGE_SIZE} cases reference this; the count would be incomplete.`,
      );
    },
  };
}

export type { CaseAct };
